import apiClient from './meta-client';
import type {
    FunnelResponse,
    FunnelDetailResponse,
    FunnelGroupCreateResponse,
    FunnelPageInfo,
    CreateFunnelRequest,
    UpdateFunnelRequest,
    FunnelValidationResponse,
    FunnelExportData,
    FunnelImportRequest,
} from '@/types/funnel';

// ========== FUNNELS API ==========

// Listar todos os funis do usuário
export const getFunnels = async (
    is_active?: boolean,
    page_id?: string
): Promise<FunnelResponse[]> => {
    const params: Record<string, string> = {};

    // Só adiciona o parâmetro se for realmente um boolean
    if (typeof is_active === 'boolean') {
        params.is_active = String(is_active);
    }

    if (page_id) {
        params.page_id = page_id;
    }

    const queryString = new URLSearchParams(params).toString();

    const response = await apiClient.get<FunnelResponse[]>(
        `/funnels${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
};

// Obter funil específico (básico)
export const getFunnel = async (funnelId: number): Promise<FunnelResponse> => {
    const response = await apiClient.get<FunnelResponse>(`/funnels/${funnelId}`);
    return response.data;
};

// Obter detalhes completos do funil (com nodes e transições)
export const getFunnelDetails = async (funnelId: number): Promise<FunnelDetailResponse> => {
    const response = await apiClient.get<FunnelDetailResponse>(`/funnels/${funnelId}/details`);
    return response.data;
};

// Criar novo funil (retorna { funnel: FunnelResponse })
export const createFunnel = async (data: CreateFunnelRequest): Promise<FunnelGroupCreateResponse> => {
    const response = await apiClient.post<FunnelGroupCreateResponse>('/funnels', data);
    return response.data;
};

// Atualizar funil
export const updateFunnel = async (
    funnelId: number,
    data: UpdateFunnelRequest
): Promise<FunnelResponse> => {
    const response = await apiClient.put<FunnelResponse>(`/funnels/${funnelId}`, data);
    return response.data;
};

// Validar funil
export const validateFunnel = async (funnelId: number): Promise<FunnelValidationResponse> => {
    const response = await apiClient.get<FunnelValidationResponse>(`/funnels/${funnelId}/validate`);
    return response.data;
};

// Ativar funil
export const activateFunnel = async (funnelId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.post<{ message: string; success: boolean }>(`/funnels/${funnelId}/activate`);
    return response.data;
};

// Desativar funil
export const deactivateFunnel = async (funnelId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.post<{ message: string; success: boolean }>(`/funnels/${funnelId}/deactivate`);
    return response.data;
};

// Duplicar funil
export const duplicateFunnel = async (funnelId: number): Promise<FunnelResponse> => {
    const response = await apiClient.post<FunnelResponse>(`/funnels/${funnelId}/duplicate`);
    return response.data;
};

// Deletar funil
export const deleteFunnel = async (funnelId: number): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete<{ message: string; success: boolean }>(`/funnels/${funnelId}`);
    return response.data;
};

// Adicionar página ao funil
export const addPageToFunnel = async (
    funnelId: number,
    internal_page_id: number
): Promise<FunnelPageInfo> => {
    const response = await apiClient.post<FunnelPageInfo>(`/funnels/${funnelId}/pages`, { internal_page_id });
    return response.data;
};

// Remover página do funil pelo `chatmeta_funnel_pages.id` (PK da tabela
// de vínculo). Use `removePageFromFunnelByInternalId` quando você tiver
// o `chatmeta_pages.id` (mais comum na UI).
export const removePageFromFunnel = async (
    funnelId: number,
    funnelPageId: number
): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete(`/funnels/${funnelId}/pages/${funnelPageId}`);
    return response.data;
};

// Remover página do funil pelo `chatmeta_pages.id` (PK da tabela pages).
// Conveniente: o backend resolve o funnel_page_id sozinho.
export const removePageFromFunnelByInternalId = async (
    funnelId: number,
    internalPageId: number
): Promise<{ message: string; success: boolean }> => {
    const response = await apiClient.delete(
        `/funnels/${funnelId}/pages/by-page/${internalPageId}`,
    );
    return response.data;
};

/** Sync de páginas no funil a partir de N categorias. Idempotente — páginas
 *  já vinculadas viram no-op. Útil pra puxar páginas novas que você
 *  categorizou depois de criar o funil. */
export interface SyncFromCategoriesResult {
    added: number;
    skipped_existing: number;
    total_pages_in_categories: number;
}
export const syncFunnelPagesFromCategories = async (
    funnelId: number,
    category_ids: number[]
): Promise<SyncFromCategoriesResult> => {
    const response = await apiClient.post<SyncFromCategoriesResult>(
        `/funnels/${funnelId}/sync-from-categories`,
        { category_ids },
    );
    return response.data;
};

// Exportar JSON do funil (nodes, buttons, triggers, transitions)
export const exportFunnel = async (funnelId: number): Promise<FunnelExportData> => {
    const response = await apiClient.get<FunnelExportData>(`/funnels/${funnelId}/export`);
    return response.data;
};

// Importar nodes de JSON em um funil existente
export const importFunnelNodes = async (data: FunnelImportRequest): Promise<FunnelDetailResponse> => {
    const response = await apiClient.post<FunnelDetailResponse>('/funnels/import', data);
    return response.data;
};

// Traduzir funil com IA
export const translateFunnel = async (
    funnelId: number,
    data: { target_language: string; source_language?: string; ai_provider: string }
): Promise<{ success: boolean; task_id: string; message: string }> => {
    const response = await apiClient.post(`/funnels/${funnelId}/translate`, data);
    return response.data;
};

// Consultar status de tradução por task_id
export const getTranslateStatus = async (
    taskId: string
): Promise<{
    task_id: string;
    status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'not_found';
    pages_done: string;
    pages_total: string;
    pages_to_translate?: string;
    pages_skipped?: string;
    nodes_total: string;
    current_page?: string;
    error: string;
}> => {
    const response = await apiClient.get(`/funnels/translate/status/${taskId}`);
    return response.data;
};

// Consultar status de tradução por funnel_id (mais prático para polling)
export const getTranslateStatusByFunnel = async (
    funnelId: number
): Promise<{
    task_id?: string;
    funnel_id: number;
    status: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
    pages_done?: string;
    pages_total?: string;
    pages_to_translate?: string;
    pages_skipped?: string;
    nodes_total?: string;
    current_page?: string;
    error?: string;
}> => {
    const response = await apiClient.get(`/funnels/${funnelId}/translate/status`);
    return response.data;
};

// Resumo de tradução dos nodes por página
export interface TranslationSummaryNode {
    id: number;
    name: string;
}

export interface TranslationSummaryPage {
    page_id: string;
    page_name?: string;
    language?: string;
    internal_category_name?: string;
    translated_nodes: number;
    total_nodes: number;
    pending_nodes?: TranslationSummaryNode[];
    status: 'completed' | 'partial' | 'pending';
}

export interface TranslationSummary {
    funnel_id: number;
    total_nodes: number;
    pages: TranslationSummaryPage[];
}

export const getTranslationSummary = async (
    funnelId: number
): Promise<TranslationSummary> => {
    const response = await apiClient.get(`/funnels/${funnelId}/translate/summary`);
    return response.data;
};

// Re-traduzir funil (force=true), opcionalmente filtrando por página
export const retranslateFunnel = async (
    funnelId: number,
    page_id?: string
): Promise<{ success: boolean; task_id: string; message: string }> => {
    const response = await apiClient.post(`/funnels/${funnelId}/retranslate`, { page_id });
    return response.data;
};

// Traduções (read-only) de um funil para uma página específica
export interface PageTranslations {
    funnel_id: number;
    page_id: string;
    nodes: Record<string, { message?: string; title?: string; subtitle?: string }>;
    buttons: Record<string, { button_text?: string }>;
}

export const getPageTranslations = async (
    funnelId: number,
    pageId: string
): Promise<PageTranslations> => {
    const response = await apiClient.get(`/funnels/${funnelId}/translations/${pageId}`);
    return response.data;
};
